import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { API, getHeaders, useBrand } from '../portalShared';

export default function BrandCalendar() {
  const brand = useBrand();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    // Use admin calendar endpoint filtered by brand's campaigns (portal dashboard already has campaign data)
    // For now, fetch from portal campaigns and construct schedule from participants
    async function fetchSchedule() {
      try {
        const { data: campaigns } = await axios.get(`${API}/portal/campaigns`, { headers: getHeaders() });
        // Collect all participants with submitted posts
        const allSchedules = [];
        for (const camp of campaigns) {
          if (camp.participants) {
            for (const p of camp.participants) {
              if (p.post_submitted_at || p.scheduled_date) {
                allSchedules.push({
                  id: p.id,
                  scheduled_date: p.scheduled_date || (p.post_submitted_at ? p.post_submitted_at.split('T')[0] : null),
                  campaign_title: camp.title,
                  executive_name: p.executive_name ? `${p.executive_name.split(' ')[0]} ${(p.executive_name.split(' ').pop() || '')[0] || ''}.` : 'Executive',
                  has_post: !!p.post_url,
                  status: p.status,
                  impressions: p.impressions,
                });
              }
            }
          }
        }
        setSchedules(allSchedules);
      } catch {} finally { setLoading(false); }
    }
    fetchSchedule();
  }, []);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const byDate = {};
  schedules.forEach(s => { if (s.scheduled_date) { if (!byDate[s.scheduled_date]) byDate[s.scheduled_date] = []; byDate[s.scheduled_date].push(s); }});

  const getDayColor = (dateStr) => {
    const items = byDate[dateStr];
    if (!items) return null;
    if (items.some(i => i.has_post)) return 'bg-[#10B981]';
    return 'bg-[#6B7280]';
  };

  const dayItems = selectedDay ? (byDate[selectedDay] || []) : [];

  if (loading) return <p className="text-[#9CA3AF]">Loading calendar...</p>;

  return (
    <div data-testid="brand-calendar">
      <h3 className="text-sm font-semibold mb-4">Content Calendar</h3>
      <div className="flex gap-6">
        {/* Grid */}
        <div className="flex-1 bg-[#111827] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonth(new Date(year, mo - 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold">{monthName}</span>
            <button onClick={() => setMonth(new Date(year, mo + 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-px mb-1">
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <div key={i} className="text-center text-[9px] font-mono text-[#6B7280] uppercase py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px">
            {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} className="h-14 bg-[#090E33]/30" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dotColor = getDayColor(dateStr);
              const isSelected = selectedDay === dateStr;
              const isToday = dateStr === new Date().toISOString().split('T')[0];
              const count = (byDate[dateStr] || []).length;

              return (
                <div key={day} onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                  className={`h-14 bg-[#090E33] border cursor-pointer transition-colors relative p-1.5
                    ${isSelected ? 'border-[#FF4343]' : isToday ? 'border-white/20' : 'border-white/5 hover:border-white/10'}`}>
                  <span className={`text-[10px] ${isToday ? 'text-[#FF4343] font-bold' : 'text-[#6B7280]'}`}>{day}</span>
                  {dotColor && (
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
                      <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                      {count > 1 && <span className="text-[8px] text-[#6B7280]">+{count - 1}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[9px] text-[#6B7280]">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#6B7280]" /> Scheduled</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Published</span>
          </div>
        </div>

        {/* Day Detail */}
        <div className="w-64 flex-shrink-0">
          {selectedDay ? (
            <div className="bg-[#111827] border border-white/10 p-4">
              <h4 className="text-xs font-semibold mb-3">{new Date(selectedDay + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
              {dayItems.length === 0 ? (
                <p className="text-[10px] text-[#6B7280]">No posts on this day.</p>
              ) : (
                <div className="space-y-2">
                  {dayItems.map(item => (
                    <div key={item.id} className={`p-3 border text-xs ${item.has_post ? 'border-[#10B981]/30 bg-[#10B981]/5' : 'border-white/10'}`}>
                      <p className="text-white font-medium">{item.executive_name}</p>
                      <p className="text-[#6B7280] text-[10px] mt-0.5">{item.campaign_title}</p>
                      {item.has_post && item.impressions ? (
                        <p className="text-[10px] text-[#10B981] mt-1 font-mono">{item.impressions.toLocaleString()} impressions</p>
                      ) : (
                        <span className={`text-[9px] font-mono uppercase mt-1 inline-block ${item.has_post ? 'text-[#10B981]' : 'text-[#6B7280]'}`}>
                          {item.has_post ? 'PUBLISHED' : 'SCHEDULED'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#111827] border border-white/10 p-4 text-center">
              <p className="text-[10px] text-[#6B7280]">Click a day to see posts</p>
            </div>
          )}

          {/* Summary Stats */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-white">{schedules.length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Total Posts</p>
            </div>
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-[#10B981]">{schedules.filter(s => s.has_post).length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Published</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


